import { useState } from "react";
import { useTranslation } from "react-i18next";
import { categories } from "@/mocks/categories";
import type { Lang } from "@/i18n/languages";

interface Props {
  lang: Lang;
}

export default function SpreadsheetPreview({ lang }: Props) {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);

  const rows = categories.slice(0, 6).map((cat, i) => ({
    product: cat.name[lang],
    category: cat.name[lang],
    seller: ["Verified", "Verified", "Pending", "Verified", "Verified", "New"][i],
    qc: ["Passed", "Passed", "In review", "Passed", "Passed", "N/A"][i],
    updated: ["2026-08", "2026-08", "2026-07", "2026-08", "2026-07", "2026-08"][i],
  }));

  return (
    <section className="w-full bg-background-100/70">
      <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-center">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              {t("section.spreadsheet.title")}
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              {t("section.spreadsheet.p1")}
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">
              {t("section.spreadsheet.p2")}
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">
              {t("section.spreadsheet.p3")}
            </p>
          </div>

          <div className="overflow-hidden rounded-lg border border-background-300/60 bg-background-50">
            <div className="flex items-center gap-2 border-b border-background-300/60 bg-background-100/70 px-4 py-3">
              <span className="w-3 h-3 flex items-center justify-center rounded-full bg-accent-400"></span>
              <span className="w-3 h-3 flex items-center justify-center rounded-full bg-secondary-400"></span>
              <span className="w-3 h-3 flex items-center justify-center rounded-full bg-primary-400"></span>
              <span className="ml-2 text-xs font-medium text-foreground-600 whitespace-nowrap">
                AllChinaBuy Spreadsheet
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs md:text-sm">
                <thead>
                  <tr className="border-b border-background-300/60 bg-background-50 text-foreground-600">
                    <th className="px-4 py-2.5 font-medium whitespace-nowrap">Product</th>
                    <th className="px-4 py-2.5 font-medium whitespace-nowrap">Seller</th>
                    <th className="px-4 py-2.5 font-medium whitespace-nowrap">QC</th>
                    <th className="px-4 py-2.5 font-medium whitespace-nowrap">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr
                      key={i}
                      onMouseEnter={() => setActive(i)}
                      className={`border-b border-background-200/50 transition-colors cursor-pointer ${
                        active === i ? "bg-primary-50" : "bg-background-50"
                      }`}
                    >
                      <td className="px-4 py-2.5 font-medium text-foreground-900 whitespace-nowrap">
                        {row.product}
                      </td>
                      <td className="px-4 py-2.5 text-foreground-700 whitespace-nowrap">
                        {row.seller}
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${
                            row.qc === "Passed"
                              ? "bg-primary-100 text-primary-800"
                              : row.qc === "In review"
                                ? "bg-accent-100 text-accent-900"
                                : "bg-secondary-100 text-secondary-900"
                          }`}
                        >
                          {row.qc}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-foreground-500 whitespace-nowrap">
                        {row.updated}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}